<template>
  <b-card no-body>
    <b-card-header class="pb-50">
      <h5>{{ $t("Filters") }}</h5>
    </b-card-header>
    <b-card-body>
      <b-row>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>{{ $t("tableHeader.name") }}</label>
          <b-form-input autofocus v-model.trim="filters.name" @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>{{ $t("Form.City") }}</label>
          <treeselect v-model="filters.cities_id" :async="true" :load-options="searchState" :normalizer="normalizer"
            @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>Zip Code</label>
          <b-form-input v-model.trim="filters.zip_codes_id" placeholder="Zip Code" @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>DOB</label>
          <flat-pickr v-model="filters.date_birth" :config="config" class="form-control" placeholder="MM-DD-YYYY" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>Date</label>
          <flat-pickr v-model="filters.create" :config="config" class="form-control" placeholder="MM-DD-YYYY" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>Email</label>
          <b-form-input v-model.trim="filters.email" @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>{{ $t("Form.Phone") }}</label>
          <b-form-input v-model.number="filters.phone" @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>{{ $t("Form.Gender") }}</label>
          <treeselect v-model="filters.gender" :options="genderOptions" :normalizer="normalizer"
            @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>{{ $t("Form.Status") }}</label>
          <treeselect v-model="filters.status" :options="statusOptions" :normalizer="normalizer"
            @keyup.enter="searchFilter" />
        </b-col>
        <b-col cols="12" md="3" class="mb-md-0 mb-2">
          <label>Facility</label>
          <treeselect v-model="filters.facilities_id" :async="true" :load-options="searchFacility" :defaultOptions="true"
            :normalizer="normalizer" @keyup.enter="searchFilter" multiple />
        </b-col>
        <b-col cols="12" class="mb-md-0 mb-2 mt-2" align="end">
          <b-button
            variant="success"
            @click="excel"
            class="mr-1"
            :disabled="loading || isExporting"
            v-if="$can('index', 'administration')">
            <span v-if="!isExporting">Excel</span>
            <span v-else>
              <SpinnerLoading />
            </span>
          </b-button>
          <b-button variant="warning" @click="resetFilter" class="mr-1">
            {{ $t("Clear") }}
          </b-button>
          <b-button variant="primary" @click="searchFilter" :disabled="loading">
            <span v-if="!loading">{{ $t("Search") }}</span>
            <span v-else>
              <SpinnerLoading />
            </span>
          </b-button>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
} from "bootstrap-vue";

import axiosC from "@/core/services/apiInsurance/admin/cities";
import axiosF from "@/core/services/apiInsurance/admin/facilities";

import Swal from 'sweetalert2'
import * as XLSX from 'xlsx/xlsx.mjs'
import patientApi from '@/core/services/apiInsurance/patient'

import SpinnerLoading from "@/components/SpinnerLoading";
import flatPickr from "vue-flatpickr-component";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormInput,
    BButton,
    flatPickr,
    SpinnerLoading,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      setTimeoutBuscador: "",
      isExporting: false,
      exportSubscription: null,
      stateOptions: [],
      filters: {
        name: null,
        cities_id: null,
        zip_codes_id: null,
        date_birth: null,
        create: null,
        email: null,
        phone: null,
        gender: null,
        status: null,
        facilities_id: null,
      },
      genderOptions: [
        { name: this.$t("Male"), id: "male" },
        { name: "Female", id: "female" },
      ],
      statusOptions: [
        { name: "Active", id: "true" },
        { name: "Inactive", id: "false" },
      ],
      config: {
        dateFormat: "m-d-Y", 
        allowInput: true,
      },
      normalizer(node) {
        return {
          id: node.id,
          label: node.name,
        };
      },
    };
  },
  beforeDestroy() {
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe()
      this.exportSubscription = null
    }
    Swal.close()
  },
  methods: {
    async excel() {
      if (this.isExporting) return

      const filtersSnapshot = JSON.parse(JSON.stringify(this.filters || {}))

      const hasActiveFilters = Object.values(filtersSnapshot).some(v => {
        if (v === null || v === undefined) return false
        if (typeof v === 'string') return v.trim() !== ''
        if (Array.isArray(v)) return v.length > 0
        return true
      })

      if (!hasActiveFilters) {
        const warningResult = await Swal.fire({
          icon: 'warning',
          title: 'Export all patients',
          text: 'No active filters. If you export all patients, this may take a few hours.',
          showCancelButton: true,
          confirmButtonText: 'Continue',
          cancelButtonText: 'Cancel',
          allowOutsideClick: false,
        })

        if (!warningResult.isConfirmed) return
      }

      this.isExporting = true

      const progressHtml = (percentage, fetchedCount, totalCount, page, totalPages) => {
        const safePercentage = Number.isFinite(percentage) ? percentage : 0
        const safeFetched = Number.isFinite(fetchedCount) ? fetchedCount : 0
        const safeTotal = Number.isFinite(totalCount) ? totalCount : 0
        const safePage = Number.isFinite(page) ? page : 0
        const safeTotalPages = Number.isFinite(totalPages) ? totalPages : 0

        const meta =
          safeTotal > 0 ? `${safeFetched}/${safeTotal} patients` : 'Loading...'

        const pageMeta = safeTotalPages > 0 ? `Page ${safePage}/${safeTotalPages}` : ''

        return `
          <div class="text-center">
            <div class="progress mb-2">
              <div class="progress-bar" role="progressbar" style="width:${safePercentage}%"></div>
            </div>
            <div class="text-muted mb-1">${safePercentage}% complete</div>
            <div class="text-muted">${meta} ${pageMeta}</div>
          </div>
        `
      }

      const title = hasActiveFilters ? 'Exporting patients (filters applied)' : 'Exporting all patients'

      const hint = hasActiveFilters
        ? 'The Excel will be generated with the filtered patients.'
        : 'No active filters: all patients will be exported. This may take a few hours.'
      const hintHtml = `<div class="mt-2 text-muted" style="font-size:.9rem">${hint}</div>`

      Swal.fire({
        icon: 'info',
        title,
        html: progressHtml(0, 0, 0, 0, 0) + hintHtml,
        allowOutsideClick: false,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      }).then(result => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          this.cancelExcel()
        }
      })

      this.exportSubscription = patientApi
        .exportPatientsProgressive(filtersSnapshot, { batchSize: 500 })
        .subscribe({
          next: event => {
            if (!event || !event.type) return

            if (event.type === 'progress') {
              const total = Number(event.totalCount || 0)
              const fetched = Number(event.fetchedCount || 0)
              const percentage = total > 0 ? Math.round((fetched / total) * 100) : 0

              Swal.update({
                html:
                  progressHtml(
                    percentage,
                    fetched,
                    total,
                    Number(event.page || 0),
                    Number(event.totalPages || 0)
                  ) + hintHtml,
              })
            }

            if (event.type === 'complete') {
              const patients = Array.isArray(event.patients) ? event.patients : []

              const dateStr = new Date().toISOString().slice(0, 10)
              const fileName = `insurance_patients_${dateStr}.xlsx`

              Swal.update({
                title: 'Generating Excel...',
                html: progressHtml(100, patients.length, patients.length, 0, 0) + hintHtml,
              })

              const serializeFilterValue = value => {
                if (value === null || value === undefined) return ''
                if (Array.isArray(value)) {
                  return value
                    .map(v => serializeFilterValue(v))
                    .filter(Boolean)
                    .join(', ')
                }
                if (typeof value === 'object') {
                  return value.label || value.name || value.id || value.value || ''
                }
                return String(value)
              }

              const filterEntries = Object.entries(filtersSnapshot)
                .filter(([, v]) => {
                  if (v === null || v === undefined) return false
                  if (typeof v === 'string') return v.trim() !== ''
                  if (Array.isArray(v)) return v.length > 0
                  return true
                })
                .map(([k, v]) => [k, serializeFilterValue(v)])

              const hasFiltersForSheet = hasActiveFilters && filterEntries.length > 0

              const patientsRows = patients.map(p => ({
                ID: p?.id ?? '',
                Name: p?.name ?? '',
                Lastname: p?.lastname ?? '',
                Identification: p?.identification ?? '',
                Email: p?.email ?? '',
                Phone: p?.phone ?? '',
                City: p?.cities?.name ?? '',
                Gender: p?.gender ?? '',
                Status: p?.status ? this.$t('Active') : this.$t('Inactive'),
                DOB: p?.date_birth ?? '',
                'Visit Number': p?.histories?.visit_number ?? '',
                'Type Insurance': p?.type_insurance ?? '',
                Membership: p?.is_membership ? 'Yes' : 'No',
              }))

              const wb = XLSX.utils.book_new()

              const patientColumns = [
                'ID',
                'Name',
                'Lastname',
                'Identification',
                'Email',
                'Phone',
                'City',
                'Gender',
                'Status',
                'DOB',
                'Visit Number',
                'Type Insurance',
                'Membership',
              ]

              const patientSheet = XLSX.utils.json_to_sheet(patientsRows, {
                header: patientColumns,
              })
              XLSX.utils.book_append_sheet(wb, patientSheet, 'Patients')

              if (hasFiltersForSheet) {
                const filterSheetRows = [['Field', 'Value'], ...filterEntries.map(([k, v]) => [k, v])]
                const filterSheet = XLSX.utils.aoa_to_sheet(filterSheetRows)
                XLSX.utils.book_append_sheet(wb, filterSheet, 'Filters')
              }

              // Download the Excel file to the user.
              XLSX.writeFile(wb, fileName)

              this.isExporting = false
              if (this.exportSubscription) this.exportSubscription.unsubscribe()
              this.exportSubscription = null

              Swal.close()

              Swal.fire({
                icon: 'success',
                title: 'Excel generated',
                text: 'The download should start automatically.',
                timer: 2000,
                showConfirmButton: false,
              })
            }
          },
          error: err => {
            this.isExporting = false
            if (this.exportSubscription) this.exportSubscription.unsubscribe()
            this.exportSubscription = null

            Swal.close()
            Swal.fire({
              icon: 'error',
              title: 'Excel export failed',
              text: 'Could not generate the Excel. Please try again.',
            })

            // eslint-disable-next-line no-console
            console.error(err)
          },
        })
    },
    cancelExcel() {
      if (!this.isExporting) return

      this.isExporting = false
      if (this.exportSubscription) this.exportSubscription.unsubscribe()
      this.exportSubscription = null

      Swal.close()
      Swal.fire({
        icon: 'info',
        title: 'Export cancelled',
        text: 'The Excel generation was stopped.',
        timer: 2000,
        showConfirmButton: false,
      })
    },
    searchFacility({ action, searchQuery, callback }) {
      if (action === "ASYNC_SEARCH") {
        const datos = {
          name: searchQuery,
        };
        axiosF.facilityFilter(30, datos).then(({ data }) => {
          callback(null, data);
        });
      }
    },
    searchFilter() {
      const data = this.filters
      this.$emit("searchFilter", JSON.parse(JSON.stringify(data)))
      const {
        name,
        cities_id,
        zip_codes_id,
        date_birth,
        create,
        email,
        phone,
        gender,
        status,
        facilities_id,
      } = this.filters
      if (
        cities_id == null &&
        zip_codes_id == null &&
        name == null &&
        date_birth == null &&
        create == null &&
        email == null &&
        phone == null &&
        gender == null &&
        status == null &&
        facilities_id == null
      ) {
        this.$emit("filterPage", false)
      } else {
        this.$emit("filterPage", true)
      }
    },
    resetFilter() {
      const filter = JSON.parse(JSON.stringify(this.filters));
      for (const iterator in filter) {
        this.filters[iterator] = null;
      }
    },
    searchState({ action, searchQuery, callback }) {
      if (action === "ASYNC_SEARCH") {
        clearTimeout(this.setTimeoutBuscador);
        const datos = {
          name: searchQuery,
        };
        this.setTimeoutBuscador = setTimeout(() => {
          axiosC.citiesFilterFilters(datos).then(({ data }) => {
            callback(null, data);
          });
        }, 350);
      }
    }
  },
};
</script>